import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTeam360, money, type Team360 as Team360Data, type Period } from "../../services/club360";
import { SectionCard, StatCard, StatGrid } from "../admin/AdminCard";
import { formatDate } from "../../lib/dateFormat";
import api from "../../services/api";
import toast from "react-hot-toast";
import TeamSessionDialog from "./TeamSessionDialog";
import TeamTrainingsCalendar from "./TeamTrainingsCalendar";

/** How somebody arrived, in words rather than a code. */
function sourceLabel(source: { kind: string; by_name: string | null; label: string | null }): string {
  if (source.kind === "invitation") {
    return source.by_name ? `invitat de ${source.by_name}` : "invitat de club";
  }
  if (source.kind === "join_link") {
    const whose = source.by_name ? `linkul lui ${source.by_name}` : "link de înscriere";
    return source.label ? `${whose} (${source.label})` : whose;
  }
  if (source.kind === "added_by_club") {
    return source.by_name ? `adăugat de ${source.by_name}` : "adăugat de club";
  }
  return "sursă necunoscută";
}

/**
 * Viziunea 360 a unei echipe.
 *
 * Everything a club admin would otherwise gather from six screens: who is in
 * the squad and how old they are, who turns up, who pays, who upgraded, what
 * the club earned from them and through whom, and who is top of the internal
 * ranking.
 *
 * The figures chosen are the ones somebody can act on. A rate out of nothing
 * is shown as "—" rather than 0%, because a team whose register was never
 * taken has not got 0% attendance — it has no attendance data, and those are
 * different problems with different fixes.
 */

const PERIODS: { key: Period; label: string }[] = [
  { key: "month", label: "Luna curentă" },
  { key: "quarter", label: "Ultimele 3 luni" },
  { key: "all", label: "Tot timpul" },
];

export default function Team360({ teamId }: { teamId: string }) {
  const navigate = useNavigate();

  const [data, setData] = useState<Team360Data | null>(null);
  const [period, setPeriod] = useState<Period>("quarter");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAmounts, setShowAmounts] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  /** Null = closed; a string = open, pre-filled with that date. */
  const [addTraining, setAddTraining] = useState<string | null>(null);

  const act = async (membershipId: string, action: "approve" | "reject") => {
    if (action === "reject" && !window.confirm("Respingi cererea? Contul rămâne, doar nu intră în echipă.")) {
      return;
    }
    setBusy(membershipId);
    try {
      await api.post(`/join-links/requests/${membershipId}/${action}`);
      toast.success(action === "approve" ? "Sportiv acceptat în echipă." : "Cerere respinsă.");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Acțiunea nu a reușit.");
    }
    setBusy(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await getTeam360(teamId, period));
    } catch {
      setError(true);
      setData(null);
    }
    setLoading(false);
  }, [teamId, period]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 rounded-full border-indigo-600 animate-spin border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 text-center bg-white border rounded-2xl border-slate-200">
        <p className="text-slate-500">Nu am putut încărca datele echipei.</p>
        <button onClick={load} className="mt-3 text-sm font-semibold text-indigo-600">
          Reîncearcă
        </button>
      </div>
    );
  }

  const { roster, attendance, sessions, payments, plans, affiliate, leaderboard } = data;
  const marked = attendance.present + attendance.absent + attendance.excused;

  const genderBar = [
    { label: "băieți", n: roster.gender.male, cls: "bg-sky-500" },
    { label: "fete", n: roster.gender.female, cls: "bg-pink-500" },
    { label: "altul", n: roster.gender.other, cls: "bg-violet-500" },
    { label: "nespecificat", n: roster.gender.unknown, cls: "bg-slate-300" },
  ].filter((x) => x.n > 0);

  const paymentChip = (status: string | undefined) => {
    if (status === "overdue")
      return <span className="px-2 py-0.5 text-[11px] font-bold rounded-full bg-rose-100 text-rose-700">Restanță</span>;
    if (status === "pending")
      return <span className="px-2 py-0.5 text-[11px] font-bold rounded-full bg-amber-100 text-amber-700">De încasat</span>;
    if (status === "paid")
      return <span className="px-2 py-0.5 text-[11px] font-bold rounded-full bg-emerald-100 text-emerald-700">La zi</span>;
    return <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-500">Fără taxă</span>;
  };

  return (
    <div className="space-y-5">
      {/* Period */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 p-1 border rounded-xl border-slate-200 bg-slate-100">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
                period === p.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => navigate(`/dashboard/admin-club/reports?team=${teamId}`)}
          className="px-3 py-2 text-sm font-semibold border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50"
        >
          Raport de prezență
        </button>
        {loading && <span className="text-xs text-slate-400">se actualizează...</span>}
      </div>

      {/* Headline figures */}
      <StatGrid>
        <StatCard
          label="Sportivi"
          value={roster.athletes}
          dotClass="bg-indigo-500"
          hint={
            roster.average_age !== null
              ? `media ${roster.average_age} ani${roster.youngest !== null ? ` · ${roster.youngest}–${roster.oldest}` : ""}`
              : "fără date de naștere"
          }
          hintTone="muted"
        />
        <StatCard
          label="Prezență"
          value={marked === 0 ? "—" : `${attendance.rate}%`}
          dotClass={attendance.rate >= 75 ? "bg-emerald-500" : attendance.rate >= 50 ? "bg-amber-500" : "bg-rose-500"}
          valueClass={marked === 0 ? "text-slate-400" : attendance.rate >= 75 ? "text-emerald-600" : "text-slate-800"}
          hint={
            marked === 0
              ? "nicio prezență pontată"
              : `${attendance.present} prezențe din ${marked} pontate`
          }
          hintTone={marked === 0 ? "down" : "muted"}
        />
        <StatCard
          label="Conturi upgrade"
          value={plans.paid}
          dotClass="bg-violet-500"
          hint={roster.athletes > 0 ? `${plans.rate}% din echipă` : "—"}
          hintTone={plans.paid > 0 ? "up" : "muted"}
        />
        <StatCard
          label="Câștig afiliere"
          value={money(affiliate.earned_cents)}
          dotClass="bg-emerald-500"
          hint={
            affiliate.referred === 0
              ? "nimeni adus prin cod"
              : `${affiliate.converted} din ${affiliate.referred} convertiți`
          }
          hintTone={affiliate.earned_cents > 0 ? "up" : "muted"}
        />
      </StatGrid>

      {/* Waiting to be accepted. Straight under the headline figures on
          purpose: these are the rows with something to do on them, and an
          admin who opens the team page should not have to scroll past
          everything else to find them. */}
      {data.pending.length > 0 && (
        <div className="bg-white border rounded-2xl border-amber-200">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-amber-100 bg-amber-50/60 rounded-t-2xl">
            <div>
              <h3 className="font-bold text-slate-800">În așteptare să fie acceptați</h3>
              <p className="text-xs text-slate-500">
                Au cont, dar nu sunt încă pe echipă — nu intră în cifrele de mai sus.
              </p>
            </div>
            <span className="px-2 py-0.5 text-xs font-bold rounded-full text-amber-700 bg-amber-100">
              {data.pending.length}
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {data.pending.map((p) => (
              <div key={p.membership_id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-[220px]">
                  <button
                    onClick={() => navigate(`/dashboard/admin-club/athletes/${p.athlete_id}`)}
                    className="font-medium text-left text-slate-800 hover:text-indigo-600"
                  >
                    {p.full_name || p.email}
                  </button>
                  <p className="text-xs text-slate-500">
                    {[p.age !== null ? `${p.age} ani` : null, sourceLabel(p.source)]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  {p.parents.length > 0 ? (
                    <p className="text-[11px] text-slate-400">
                      {p.parents
                        .map((pa) => `${pa.full_name || pa.email}${pa.phone ? ` · ${pa.phone}` : ""}`)
                        .join(" · ")}
                    </p>
                  ) : (
                    <p className="text-[11px] text-slate-400">
                      {p.phone || "fără telefon și fără părinte legat"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => act(p.membership_id, "approve")}
                    disabled={busy === p.membership_id}
                    className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Acceptă
                  </button>
                  <button
                    onClick={() => act(p.membership_id, "reject")}
                    disabled={busy === p.membership_id}
                    className="px-3 py-1.5 text-xs font-semibold border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Respinge
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Squad make-up */}
        <SectionCard title="Componența echipei" bodyPadded>
          <div className="space-y-3">
            {genderBar.length > 0 ? (
              <>
                <div className="flex h-3 overflow-hidden rounded-full bg-slate-100">
                  {genderBar.map((g) => (
                    <div
                      key={g.label}
                      className={g.cls}
                      style={{ width: `${(g.n / roster.athletes) * 100}%` }}
                      title={`${g.n} ${g.label}`}
                    />
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 text-sm">
                  {genderBar.map((g) => (
                    <span key={g.label} className="inline-flex items-center gap-1.5 text-slate-600">
                      <span className={`w-2 h-2 rounded-full ${g.cls}`} />
                      {g.n} {g.label}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500">Niciun sportiv în echipă.</p>
            )}

            <dl className="grid grid-cols-2 gap-3 pt-2 text-sm border-t border-slate-100">
              <div>
                <dt className="text-slate-500">Media de vârstă</dt>
                <dd className="font-semibold text-slate-800">
                  {roster.average_age !== null ? `${roster.average_age} ani` : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Categoria</dt>
                <dd className="font-semibold text-slate-800">
                  {data.team.age_group || "—"}
                  {data.team.age_bounds && (data.team.age_bounds.min !== null || data.team.age_bounds.max !== null) && (
                    <span className="ml-1 font-normal text-slate-400">
                      ({data.team.age_bounds.min ?? "?"}–{data.team.age_bounds.max ?? "?"})
                    </span>
                  )}
                </dd>
              </div>
            </dl>

            {/* Only shown when there is something to fix. */}
            {(roster.outside_age_group > 0 || roster.without_phone > 0 || roster.unconfirmed > 0) && (
              <div className="pt-2 space-y-1 text-sm border-t border-slate-100">
                {roster.outside_age_group > 0 && (
                  <p className="text-amber-700">
                    {roster.outside_age_group} sportivi în afara categoriei de vârstă a echipei.
                  </p>
                )}
                {roster.without_phone > 0 && (
                  <p className="text-amber-700">{roster.without_phone} fără număr de telefon.</p>
                )}
                {roster.unconfirmed > 0 && (
                  <p className="text-amber-700">
                    {roster.unconfirmed} cu emailul neconfirmat — n-au intrat niciodată în cont.
                  </p>
                )}
              </div>
            )}
          </div>
        </SectionCard>

        {/* Trainings + coaches */}
        <SectionCard
          title="Antrenamente"
          actions={
            <>
              <button
                onClick={() => setShowCalendar(true)}
                className="px-2.5 py-1.5 text-xs font-semibold border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Calendar
              </button>
              <button
                onClick={() => setAddTraining("")}
                className="px-2.5 py-1.5 text-xs font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                + Adaugă
              </button>
            </>
          }
          bodyPadded
        >
          <div className="space-y-3">
            <dl className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <dt className="text-slate-500">În perioadă</dt>
                <dd className="text-lg font-bold text-slate-800">{sessions.in_period}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Programate</dt>
                <dd className="text-lg font-bold text-slate-800">{sessions.upcoming}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Total</dt>
                <dd className="text-lg font-bold text-slate-800">{sessions.total}</dd>
              </div>
            </dl>

            {sessions.last_at ? (
              <p className="text-sm text-slate-600">
                Ultimul: <span className="font-medium">{formatDate(sessions.last_at)}</span>
                {sessions.last_title ? ` · ${sessions.last_title}` : ""}
              </p>
            ) : (
              <p className="text-sm text-slate-500">Nicio sesiune ținută încă.</p>
            )}

            {attendance.unmarked_sessions > 0 && (
              // The finding that matters most about attendance: not a low rate,
              // but a register nobody filled in.
              <p className="text-sm text-amber-700">
                {attendance.unmarked_sessions} din {sessions.in_period} sesiuni fără prezență pontată.
              </p>
            )}

            <div className="pt-2 border-t border-slate-100">
              <p className="mb-1 text-xs font-bold tracking-wide uppercase text-slate-400">Antrenori</p>
              {data.coaches.length === 0 ? (
                <p className="text-sm text-rose-600">Nicio persoană alocată — echipa nu are antrenor.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.coaches.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => navigate(`/admin/users/${c.id}`)}
                      className="px-2.5 py-1 text-sm font-medium rounded-lg bg-amber-50 text-amber-800 hover:bg-amber-100"
                    >
                      {c.full_name || c.email}
                      {c.is_head && <span className="ml-1 text-[11px] text-amber-600">principal</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </SectionCard>

        {/* Money */}
        <SectionCard
          title="Taxe și servicii"
          actions={
            <button
              onClick={() => setShowAmounts((v) => !v)}
              className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
            >
              {showAmounts ? "Ascunde sumele" : "Arată sumele"}
            </button>
          }
          bodyPadded
        >
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-slate-500">De încasat</dt>
              <dd className={`font-semibold ${payments.outstanding_cents > 0 ? "text-amber-600" : "text-slate-800"}`}>
                {showAmounts ? money(payments.outstanding_cents) : payments.outstanding_cents > 0 ? "•••" : "0"}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Încasat luna asta</dt>
              <dd className="font-semibold text-emerald-700">
                {showAmounts ? money(payments.collected_this_month_cents) : "•••"}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Restanțieri</dt>
              <dd className={`font-semibold ${payments.overdue_athletes > 0 ? "text-rose-600" : "text-slate-800"}`}>
                {payments.overdue_athletes}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Fără taxă alocată</dt>
              <dd className="font-semibold text-slate-800">{payments.without_product}</dd>
            </div>
          </dl>
          <p className="mt-3 text-[11px] text-slate-400">
            Sumele sunt ascunse implicit: lista se deschide des cu cineva lângă tine.
          </p>
        </SectionCard>

        {/* Affiliate */}
        <SectionCard title="Afiliere" bodyPadded>
          {affiliate.referred === 0 ? (
            <p className="text-sm text-slate-500">
              Niciun sportiv din echipă nu a venit prin cod de afiliere.
            </p>
          ) : (
            <div className="space-y-3 text-sm">
              <dl className="grid grid-cols-2 gap-3">
                <div>
                  <dt className="text-slate-500">Direct la club</dt>
                  <dd className="font-semibold text-slate-800">
                    {money(affiliate.earned_direct_cents)}
                    <span className="ml-1 font-normal text-slate-400">
                      ({affiliate.by_source.club} aduși)
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Prin antrenor</dt>
                  <dd className="font-semibold text-slate-800">
                    {money(affiliate.earned_via_coach_cents)}
                    <span className="ml-1 font-normal text-slate-400">
                      ({affiliate.by_source.coach} aduși)
                    </span>
                  </dd>
                </div>
              </dl>
              {(affiliate.by_source.parent > 0 || affiliate.by_source.athlete > 0) && (
                <p className="text-slate-500">
                  Alți {affiliate.by_source.parent + affiliate.by_source.athlete} aduși de părinți sau
                  sportivi — acolo recompensa merge la ei, nu la club.
                </p>
              )}
              <p className="pt-2 text-[11px] border-t border-slate-100 text-slate-400">
                Sumele sunt ce a acumulat CLUBUL. Recompensa personală a unui antrenor nu e
                numărată aici.
              </p>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Internal ranking */}
      <SectionCard title="Clasament intern" bodyPadded>
        {leaderboard.length === 0 || leaderboard.every((l) => l.points === 0) ? (
          <p className="text-sm text-slate-500">Nimeni nu are puncte încă.</p>
        ) : (
          <ol className="space-y-1">
            {leaderboard
              .filter((l) => l.points > 0)
              .map((l) => (
                <li key={l.athlete_id} className="flex items-center gap-3 py-1.5 text-sm">
                  <span
                    className={`flex items-center justify-center w-7 h-7 text-xs font-bold rounded-full flex-shrink-0 ${
                      l.rank === 1
                        ? "bg-amber-100 text-amber-700"
                        : l.rank === 2
                          ? "bg-slate-200 text-slate-700"
                          : l.rank === 3
                            ? "bg-orange-100 text-orange-700"
                            : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {l.rank}
                  </span>
                  <button
                    onClick={() => navigate(`/dashboard/admin-club/athletes/${l.athlete_id}`)}
                    className="flex-1 min-w-0 font-medium text-left truncate text-slate-800 hover:text-indigo-600"
                  >
                    {l.full_name}
                  </button>
                  <span className="font-bold tabular-nums text-slate-700">{l.points}</span>
                </li>
              ))}
          </ol>
        )}
      </SectionCard>

      {/* The squad */}
      <SectionCard title={`Sportivi (${data.athletes.length})`}>
        {data.athletes.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">Niciun sportiv în echipă.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 font-semibold text-slate-600">Sportiv</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">Vârstă</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">Prezență</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">Puncte</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">Plan</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">Taxe</th>
                  <th className="hidden px-4 py-3 font-semibold lg:table-cell text-slate-600">Afiliere</th>
                  <th className="hidden px-4 py-3 font-semibold md:table-cell text-slate-600">Telefon</th>
                </tr>
              </thead>
              <tbody>
                {data.athletes.map((a) => {
                  const aMarked = a.attendance.present + a.attendance.absent + a.attendance.excused;
                  return (
                    <tr key={a.athlete_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/dashboard/admin-club/athletes/${a.athlete_id}`)}
                          className="font-medium text-left text-slate-800 hover:text-indigo-600"
                        >
                          {a.full_name || a.email}
                        </button>
                        {a.email_verified === false && (
                          <span className="block text-[11px] font-semibold text-amber-600">neconfirmat</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">{a.age ?? "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {aMarked === 0 ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <span
                            className={`font-semibold tabular-nums ${
                              a.attendance.rate >= 75
                                ? "text-emerald-700"
                                : a.attendance.rate >= 50
                                  ? "text-amber-600"
                                  : "text-rose-600"
                            }`}
                          >
                            {a.attendance.rate}%
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                        {a.points}
                        {a.rank && a.points > 0 && (
                          <span className="ml-1 text-[11px] text-slate-400">#{a.rank}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {a.plan?.paid ? (
                          <span className="px-2 py-0.5 text-[11px] font-bold rounded-full bg-violet-100 text-violet-700 capitalize">
                            {a.plan.plan_code}
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-400">free</span>
                        )}
                      </td>
                      <td className="px-4 py-3">{paymentChip(a.payment?.status)}</td>
                      <td className="hidden px-4 py-3 lg:table-cell">
                        {a.referral ? (
                          <span className="text-slate-600">
                            {a.referral.source === "club"
                              ? "club"
                              : a.referral.source === "coach"
                                ? `antrenor${a.referral.by_name ? `: ${a.referral.by_name}` : ""}`
                                : a.referral.source}
                            {a.referral.club_earned_cents > 0 && showAmounts && (
                              <span className="block text-[11px] text-emerald-700">
                                {money(a.referral.club_earned_cents)}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="hidden px-4 py-3 md:table-cell text-slate-600 whitespace-nowrap">
                        {a.phone || <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {showCalendar && (
        <TeamTrainingsCalendar
          teamId={teamId}
          teamName={data.team.name}
          onClose={() => setShowCalendar(false)}
          onAddForDay={(isoDate) => {
            // Hand the day over to the add form rather than opening both: two
            // stacked modals is a trap on a phone.
            setShowCalendar(false);
            setAddTraining(isoDate);
          }}
        />
      )}

      {addTraining !== null && (
        <TeamSessionDialog
          teamId={teamId}
          teamName={data.team.name}
          clubId={data.team.club.id}
          coaches={data.coaches}
          initialDate={addTraining || undefined}
          onClose={() => setAddTraining(null)}
          // The session counts and the unmarked-register warning are both on
          // this screen, so they have to be refetched.
          onCreated={load}
        />
      )}
    </div>
  );
}
